import { useState } from "react";
import { ArrowRight, ArrowLeft, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { TIERS, SITE } from "@/content/copy";
import { trackEvent } from "@/lib/analytics";
import { api, formatApiError } from "@/lib/api";

const QUESTIONS = [
  {
    id: "payroll",
    label: "Payroll",
    question: "Do you run payroll through the business?",
    options: ["No employees", "Yes - up to 5 staff", "Yes - 6 to 15 staff", "More than 15 staff"],
  },
  {
    id: "need",
    label: "Biggest need",
    question: "What would make the biggest difference right now?",
    options: [
      "Just get the books right and keep them tidy",
      "A proper month-end close with reports I actually understand",
      "Cashflow forecasts, budgets and job profitability",
      "A senior finance brain for strategy, boards or investors",
    ],
  },
  {
    id: "volume",
    label: "Monthly transactions",
    question: "Roughly how many bank transactions a month?",
    options: ["Under 150", "150 to 300", "300 to 600", "Over 600 - or no idea"],
  },
];

const EMAIL_STEP = QUESTIONS.length;

function recommend(answers) {
  let tier = 0;
  if (answers.payroll > 0) tier = Math.max(tier, 1);
  if (answers.need >= 1) tier = Math.max(tier, answers.need);
  if (answers.volume === 2) tier = Math.max(tier, 2);
  if (answers.volume === 3) return { custom: true, tier };
  return { custom: false, tier };
}

export default function PlanQuiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isEmailStep = step === EMAIL_STEP;
  const result = submitted ? recommend(answers) : null;
  const tier = result && !result.custom ? TIERS.items[result.tier] : null;

  const answer = (qid, idx) => {
    setAnswers({ ...answers, [qid]: idx });
    setStep(step + 1);
    trackEvent("plan_quiz_answer", { question: qid, option: idx });
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    const res = recommend(answers);
    const rec = res.custom ? "Custom scope (600+ transactions)" : TIERS.items[res.tier].name;
    const readableAnswers = Object.fromEntries(
      QUESTIONS.map((q) => [q.label, q.options[answers[q.id]]])
    );
    try {
      await api.post("/leads/quiz", {
        name: name || null,
        email,
        answers: readableAnswers,
        recommendation: rec,
        promise: res.custom ? "Tailored scope" : TIERS.items[res.tier].promise,
      });
      trackEvent("quiz_lead", { recommended: rec });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setName("");
    setEmail("");
    setSubmitted(false);
    setStep(0);
  };

  return (
    <section data-testid="plan-quiz-section" className="py-20 lg:py-28 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <p className="text-sm font-bold uppercase tracking-widest text-terracotta">Not sure which plan?</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-navy">
            Answer 3 questions, get a recommendation
          </h2>
        </div>

        <div className="mt-10 rounded-2xl bg-background border border-border p-8 lg:p-10">
          {!isEmailStep && !submitted && (
            <div data-testid={`quiz-step-${step + 1}`}>
              <p className="text-xs font-bold uppercase tracking-widest text-navy/40">
                Question {step + 1} of {QUESTIONS.length}
              </p>
              <h3 className="mt-2 text-xl font-bold text-navy">{QUESTIONS[step].question}</h3>
              <div className="mt-6 grid grid-cols-1 gap-3">
                {QUESTIONS[step].options.map((option, idx) => (
                  <button
                    key={option}
                    data-testid={`quiz-option-${step + 1}-${idx + 1}`}
                    onClick={() => answer(QUESTIONS[step].id, idx)}
                    className="flex items-center justify-between rounded-xl bg-white border-2 border-navy/10 px-5 py-4 text-left text-base font-semibold text-navy transition-all duration-200 hover:border-terracotta hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:outline-none"
                  >
                    {option}
                    <ArrowRight className="w-4 h-4 text-terracotta shrink-0" aria-hidden="true" />
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button
                  data-testid="quiz-back-button"
                  onClick={() => setStep(step - 1)}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-navy/50 hover:text-navy transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back
                </button>
              )}
            </div>
          )}

          {isEmailStep && !submitted && (
            <form data-testid="quiz-email-form" onSubmit={submitEmail}>
              <h3 className="text-xl font-bold text-navy">Where should we send your recommendation?</h3>
              <p className="mt-2 text-sm text-navy/60">
                You'll see it on the next screen too - we email a copy so you can think it over.
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label htmlFor="quiz-name" className="sr-only">First name (optional)</label>
                <input
                  id="quiz-name"
                  data-testid="quiz-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="First name (optional)"
                  autoComplete="given-name"
                  className="rounded-xl border-2 border-navy/15 bg-white px-4 py-3.5 text-base text-navy placeholder:text-navy/40 focus:border-terracotta focus:outline-none"
                />
                <label htmlFor="quiz-email" className="sr-only">Email address</label>
                <input
                  id="quiz-email"
                  data-testid="quiz-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@business.com.au"
                  autoComplete="email"
                  className="rounded-xl border-2 border-navy/15 bg-white px-4 py-3.5 text-base text-navy placeholder:text-navy/40 focus:border-terracotta focus:outline-none"
                />
              </div>
              <button
                type="submit"
                data-testid="quiz-email-submit"
                disabled={loading}
                className="mt-4 w-full sm:w-auto rounded-full bg-terracotta px-8 py-3.5 text-base font-bold text-white transition-all duration-300 hover:bg-terracotta-hover hover:-translate-y-0.5 disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                {loading ? "One moment…" : "Show my recommendation"}
              </button>
              <p className="mt-3 text-xs text-navy/50">No spam, ever. Unsubscribe any time.</p>
            </form>
          )}

          {submitted && (
            <div data-testid="quiz-result" className="text-center">
              {result.custom ? (
                <>
                  <h3 className="text-2xl font-extrabold text-navy">Yours sounds like a custom scope</h3>
                  <p className="mt-4 text-base text-navy/70 leading-relaxed max-w-md mx-auto">
                    Over 600 transactions a month sits above our standard plan caps - that usually means a tailored
                    scope at Partner level or above. We've emailed you the details; let's work it out properly on a free call.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-bold uppercase tracking-widest text-terracotta">Our recommendation</p>
                  <h3 className="mt-2 text-3xl font-extrabold text-navy">{tier.name} - {tier.price} + GST</h3>
                  <p className="mt-3 text-base text-navy/70 leading-relaxed max-w-md mx-auto">{tier.promise}</p>
                  <p className="mt-3 text-sm text-navy/60">A copy is on its way to your inbox.</p>
                </>
              )}
              <p className="mt-4 text-xs text-navy/50">
                Indicative only - we confirm the right fit on your free call before anything is agreed.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="#book"
                  data-testid="quiz-book-button"
                  onClick={() => trackEvent("book_consultation_click", { location: "plan_quiz", recommended: tier?.name || "custom" })}
                  className="inline-flex justify-center items-center rounded-full bg-terracotta px-8 py-3.5 text-base font-bold text-white transition-all duration-300 hover:bg-terracotta-hover hover:-translate-y-0.5 hover:shadow-lg"
                >
                  {SITE.consultLabel}
                </a>
                <button
                  data-testid="quiz-restart-button"
                  onClick={reset}
                  className="inline-flex justify-center items-center gap-2 rounded-full border-2 border-navy/15 px-6 py-3.5 text-sm font-bold text-navy/60 hover:text-navy transition-colors"
                >
                  <RotateCcw className="w-4 h-4" aria-hidden="true" /> Start again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
